import {
  Button,
  ConfirmDialog,
  DatePicker,
  DropdownSelect,
  EmptyState,
  LoadingState,
  PaginationControls,
} from '@components/common';
import {
  useAccountActivity,
  useAccountingFinancials,
  useAccountingGuestOrigins,
  useAccountingRecentJournals,
  useAccountingSummaryKpis,
  useAccountOptions,
  useAccounts,
  useAccountSummary,
  useDeleteAccount,
  useDeleteJournalEntry,
  useGeneralLedger,
  useJournalEntries,
  usePostJournalEntry,
  useReverseJournalEntry,
  useSaveAccount,
  useSaveJournalEntry,
  useTrialBalance,
} from '@hooks/accounting';
import { useAppSelector } from '@hooks/useRedux';
import { queryClient } from '@lib/queryClient';
import { queryKeys } from '@lib/queryKeys';
import { selectCurrentUser } from '@store/auth/authSelectors';
import {
  selectCanUseCompanyScope,
  selectEffectiveOutlet,
  selectEffectiveOutletId,
} from '@store/outlets/outletSelectors';
import { BedDouble, ShoppingCart } from 'lucide-react';
import React, {
  useCallback,
  useDeferredValue,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  fetchJournalEntries,
  fetchJournalEntryById,
} from '../../../services/journalService';
import {
  AccountRecord,
  AccountSummaryCounts,
  AccountType,
  JournalEntryLineRecord,
  JournalEntryRecord,
  SaveAccountRequest,
} from '../../../types/accounting';
import { apiError } from '../../../util/notify';
import {
  AccountingOnboardingModal,
  AccountingPageInfoButton,
} from './AccountingHelp';
import { AccountEditorModal } from './accounts/AccountEditorModal';
import { AccountsSummaryCards } from './accounts/AccountsSummaryCards';
import { AccountsTable } from './accounts/AccountsTable';
import { AccountsToolbar } from './accounts/AccountsToolbar';
import {
  AccountFormState,
  createAccountFormFromRecord,
  createEmptyAccountForm,
} from './accounts/accountForm';
import { AccountingActionCenter } from './dashboard/AccountingActionCenter';
import { AccountingActivityFeed } from './dashboard/AccountingActivityFeed';
import { AccountingCashflowChartCard } from './dashboard/AccountingCashflowChartCard';
import { AccountingDashboardKpiCards } from './dashboard/AccountingDashboardKpiCards';
import { AccountingDashboardToolbar } from './dashboard/AccountingDashboardToolbar';
import { AccountingMarketSnapshot } from './dashboard/AccountingMarketSnapshot';
import { AccountingPerformanceCore } from './dashboard/AccountingPerformanceCore';
import { AccountingRevenueCompositionCard } from './dashboard/AccountingRevenueCompositionCard';
import { AccountingSalesGrowthCard } from './dashboard/AccountingSalesGrowthCard';
import {
  AccountingDashboardPeriodPreset,
  buildDashboardDateRange,
  formatCompactCurrency,
  formatCurrencyValue,
} from './dashboard/accountingDashboard';
import { JournalEntriesTable } from './journals/JournalEntriesTable';
import { JournalEntryDetailModal } from './journals/JournalEntryDetailModal';
import { JournalEntryEditorModal } from './journals/JournalEntryEditorModal';
import { ReverseJournalEntryModal } from './journals/ReverseJournalEntryModal';
import {
  createEmptyJournalEntryForm,
  createJournalEntryFormFromRecord,
  JournalEntryFormState,
} from './journals/journalForm';
import { AccountsPayablePage as RealAccountsPayablePage } from './payables/AccountsPayablePage';
import { AccountActivityTable } from './reports/AccountActivityTable';
import { AccountingReportsToolbar } from './reports/AccountingReportsToolbar';
import { GeneralLedgerTable } from './reports/GeneralLedgerTable';
import { TrialBalanceTable } from './reports/TrialBalanceTable';
import {
  AccountingReportTab,
  buildAccountActivityCsv,
  buildGeneralLedgerCsv,
  buildJournalEntriesCsv,
  buildTrialBalanceCsv,
} from './reports/accountingReports';
import {
  AccountingPeriodsSettingsPage,
  FinancialPostingRulesSettingsPage,
  PaymentMappingsSettingsPage,
  TaxRatesSettingsPage,
  TaxRefundsSettingsPage,
} from './settings/AccountingSettingsPages';

export { ExpensesReport as ExpensesReportPage } from './reports/ExpensesReport';
export {
  CogsReport as CogsReportPage,
  DeferredRevenueReport as DeferredRevenuePage,
  ProfitAndLossReport as ProfitAndLossPage,
  TaxVatReport as TaxReportPage,
} from './reports/FinancialReportTabs';

// ─── Dashboard ────────────────────────────────────────────────────────────────

export const AccountingDashboardPage: React.FC = () => {
  const currentUser = useAppSelector(selectCurrentUser);
  const effectiveOutletId = useAppSelector(selectEffectiveOutletId);
  const effectiveOutlet = useAppSelector(selectEffectiveOutlet);
  const canUseCompanyScope = useAppSelector(selectCanUseCompanyScope);
  const companyId = currentUser?.company?.id ?? null;
  const defaultRange = buildDashboardDateRange('30d');

  const [isOnboardingOpen, setIsOnboardingOpen] = useState(false);
  const [periodPreset, setPeriodPreset] =
    useState<AccountingDashboardPeriodPreset>('30d');
  const [dateFrom, setDateFrom] = useState(defaultRange.dateFrom);
  const [dateTo, setDateTo] = useState(defaultRange.dateTo);

  const {
    data: kpis,
    isLoading: isKpisLoading,
    error: kpisError,
    refetch: refetchKpis,
  } = useAccountingSummaryKpis(companyId, effectiveOutletId, dateFrom, dateTo);

  const {
    data: financials,
    isLoading: isFinancialsLoading,
    error: financialsError,
    refetch: refetchFinancials,
  } = useAccountingFinancials(companyId, effectiveOutletId, dateFrom, dateTo);

  const {
    data: recentJournals,
    isLoading: isRecentLoading,
    error: recentError,
    refetch: refetchRecent,
  } = useAccountingRecentJournals(
    companyId,
    effectiveOutletId,
    dateFrom,
    dateTo,
  );

  const {
    data: guestOrigins,
    isLoading: isGuestOriginsLoading,
    error: guestOriginsError,
    refetch: refetchGuestOrigins,
  } = useAccountingGuestOrigins(companyId, effectiveOutletId, dateFrom, dateTo);

  useEffect(() => {
    if (kpisError || financialsError || recentError || guestOriginsError) {
      apiError("Some items didn't load successfully");
    }
  }, [kpisError, financialsError, recentError, guestOriginsError]);

  const handlePresetChange = (value: AccountingDashboardPeriodPreset) => {
    setPeriodPreset(value);
    if (value === 'custom') return;
    const nextRange = buildDashboardDateRange(value);
    setDateFrom(nextRange.dateFrom);
    setDateTo(nextRange.dateTo);
  };

  const refetch = useCallback(() => {
    refetchKpis();
    refetchFinancials();
    refetchRecent();
    refetchGuestOrigins();
  }, [refetchKpis, refetchFinancials, refetchRecent, refetchGuestOrigins]);

  const snapshot = useMemo(() => {
    const defaultFinancials = {
      financialDaily: [],
      cashBalanceDaily: [],
      revenueGrowthDaily: [],
      accommodationDaily: [],
      trialBalanceAccounts: 0,
      trialBalanceNetBalance: 0,
      totalIncome: 0,
      totalExpenses: 0,
      totalCogs: 0,
      netIncome: 0,
      netCashMovement: 0,
      cashPosition: 0,
      cashInflow: 0,
      cashOutflow: 0,
      openingCashBalance: 0,
      closingCashBalance: 0,
      occupancyRate: 0,
      averageDailyRate: 0,
      averageRevPar: 0,
      averageTicketSize: 0,
      averageBookingValue: 0,
      totalTransactions: 0,
      totalJournalLines: 0,
      totalSettledOrders: 0,
      totalSettlements: 0,
      totalActiveBookings: 0,
    };

    const defaultKpis = {
      totalAccounts: 0,
      activeAccounts: 0,
      openPeriods: 0,
      closedPeriods: 0,
      draftJournals: 0,
      postedJournals: 0,
      reversedJournals: 0,
      configuredPaymentMappings: 0,
      configuredPostingRules: 0,
      activeTaxRates: 0,
      upcomingCheckouts: 0,
      unpaidBookings: 0,
      overdueReceivables: 0,
      unmappedPaymentMethods: 0,
      recentlyUsedUnmappedPaymentMethods: 0,
      failedFinancialEvents: 0,
      pendingFinancialEvents: 0,
      unmatchedBookingComPayouts: 0,
    };

    return {
      ...(kpis || defaultKpis),
      ...(financials || defaultFinancials),
      recentJournals: recentJournals || [],
      guestOrigins: guestOrigins || [],
    };
  }, [kpis, financials, recentJournals, guestOrigins]);

  const scopeLabel =
    canUseCompanyScope && !effectiveOutletId
      ? 'Viewing company-wide accounting activity across all outlets.'
      : `Viewing accounting activity for ${effectiveOutlet?.name || 'the current outlet'}.`;

  const posIncome = snapshot.revenueGrowthDaily.reduce(
    (sum, r) => sum + r.pos_collected_amount,
    0,
  );
  const accommodationIncome = snapshot.revenueGrowthDaily.reduce(
    (sum, r) => sum + r.accommodation_revenue_amount,
    0,
  );
  const totalForShare = posIncome + accommodationIncome;

  const isRefreshing =
    isKpisLoading ||
    isFinancialsLoading ||
    isRecentLoading ||
    isGuestOriginsLoading;

  return (
    <div className="space-y-4">
      <AccountingDashboardToolbar
        scopeLabel={scopeLabel}
        preset={periodPreset}
        dateFrom={dateFrom}
        dateTo={dateTo}
        isRefreshing={isRefreshing}
        onPresetChange={handlePresetChange}
        onDateFromChange={(value) => {
          setPeriodPreset('custom');
          setDateFrom(value);
        }}
        onDateToChange={(value) => {
          setPeriodPreset('custom');
          setDateTo(value);
        }}
        onRefresh={refetch}
      />

      <div
        className={
          isKpisLoading || isFinancialsLoading
            ? 'animate-pulse opacity-60 pointer-events-none'
            : ''
        }
      >
        <AccountingDashboardKpiCards snapshot={snapshot} />
      </div>

      <div
        className={`grid gap-3 md:grid-cols-2 ${isFinancialsLoading ? 'animate-pulse opacity-60' : ''}`}
      >
        {(
          [
            {
              key: 'pos',
              label: 'POS Sales',
              icon: ShoppingCart,
              value: posIncome,
            },
            {
              key: 'accommodation',
              label: 'Accommodation',
              icon: BedDouble,
              value: accommodationIncome,
            },
          ] as const
        ).map((item) => {
          const Icon = item.icon;
          const share =
            totalForShare > 0 ? (item.value / totalForShare) * 100 : 0;
          return (
            <div
              key={item.key}
              className="rounded-[20px] border border-gray-200 bg-white p-3.5 shadow-sm"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-xs font-medium text-slate-500">
                    {item.label}
                  </p>
                  <p className="mt-1.5 text-[1.75rem] font-semibold tracking-tight text-slate-900">
                    {formatCompactCurrency(item.value)}
                  </p>
                  <p className="mt-1.5 text-[11px] text-slate-500">
                    {formatCurrencyValue(item.value)}
                  </p>
                </div>
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-pos-primary/10 text-pos-primary">
                  <Icon size={18} />
                </div>
              </div>
              <div className="mt-3">
                <div className="mb-1 flex items-center justify-between">
                  <p className="text-[10px] text-slate-400">Share of revenue</p>
                  <p className="text-[10px] font-medium text-slate-500">
                    {share.toFixed(1)}%
                  </p>
                </div>
                <div className="h-1 overflow-hidden rounded-full bg-slate-100">
                  <div
                    className="h-full rounded-full bg-pos-primary"
                    style={{ width: `${share}%` }}
                  />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div
        className={`grid gap-4 xl:grid-cols-[1.5fr_0.7fr] ${isFinancialsLoading ? 'animate-pulse opacity-60' : ''}`}
      >
        <AccountingCashflowChartCard rows={snapshot.financialDaily} />
        <AccountingRevenueCompositionCard
          income={snapshot.totalIncome}
          expenses={snapshot.totalExpenses + snapshot.totalCogs}
          balance={snapshot.closingCashBalance}
        />
      </div>

      <div className={isFinancialsLoading ? 'animate-pulse opacity-60' : ''}>
        <AccountingSalesGrowthCard rows={snapshot.revenueGrowthDaily} />
      </div>

      <div
        className={`grid gap-4 xl:grid-cols-[1.4fr_0.9fr] ${isFinancialsLoading || isKpisLoading ? 'animate-pulse opacity-60' : ''}`}
      >
        <AccountingPerformanceCore
          revenueRows={snapshot.revenueGrowthDaily}
          accommodationRows={snapshot.accommodationDaily}
          totalExpenses={snapshot.totalExpenses + snapshot.totalCogs}
          averageDailyRate={snapshot.averageDailyRate}
          averageRevPar={snapshot.averageRevPar}
          averageTicketSize={snapshot.averageTicketSize}
          averageBookingValue={snapshot.averageBookingValue}
        />
        <AccountingActionCenter snapshot={snapshot} />
      </div>

      <div className="grid gap-4 xl:grid-cols-[1.2fr_0.9fr]">
        <div className={isRecentLoading ? 'animate-pulse opacity-60' : ''}>
          <AccountingActivityFeed entries={snapshot.recentJournals} />
        </div>
        <div
          className={isGuestOriginsLoading ? 'animate-pulse opacity-60' : ''}
        >
          <AccountingMarketSnapshot rows={snapshot.guestOrigins} />
        </div>
      </div>

      <AccountingOnboardingModal
        isOpen={isOnboardingOpen}
        onClose={() => setIsOnboardingOpen(false)}
      />
    </div>
  );
};

// ─── Chart of Accounts ────────────────────────────────────────────────────────

export const ChartOfAccountsPage: React.FC = () => {
  const currentUser = useAppSelector(selectCurrentUser);
  const companyId = currentUser?.company?.id ?? null;

  const [searchQuery, setSearchQuery] = useState('');
  const deferredSearchQuery = useDeferredValue(searchQuery);
  const [typeFilter, setTypeFilter] = useState<'all' | AccountType>('all');
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingAccount, setEditingAccount] = useState<AccountRecord | null>(
    null,
  );
  const [accountToDelete, setAccountToDelete] = useState<AccountRecord | null>(
    null,
  );

  const accountsQuery = useAccounts({
    companyId: companyId ?? 0,
    page,
    pageSize,
    search: deferredSearchQuery,
    type: typeFilter,
  });

  const summaryQuery = useAccountSummary(companyId, deferredSearchQuery);
  const optionsQuery = useAccountOptions(companyId);

  const { mutateAsync: saveAccountAsync, isPending: isSaving } =
    useSaveAccount();
  const { mutateAsync: deleteAccountAsync, isPending: isDeleting } =
    useDeleteAccount();

  const accounts = accountsQuery.data?.data ?? [];
  const pagination = accountsQuery.data?.pagination;
  const allAccounts = optionsQuery.data ?? [];
  const summary: AccountSummaryCounts = summaryQuery.data ?? {
    totalAccounts: 0,
    assetAccounts: 0,
    liabilityAccounts: 0,
    revenueAccounts: 0,
  };

  useEffect(() => {
    setPage(1);
  }, [deferredSearchQuery, typeFilter]);

  const handleCreate = () => {
    setEditingAccount(null);
    setIsModalOpen(true);
  };

  const handleEdit = (account: AccountRecord) => {
    setEditingAccount(account);
    setIsModalOpen(true);
  };

  const handleSave = async (form: AccountFormState) => {
    if (!companyId) {
      apiError('Company details are missing for this account.');
      return;
    }

    const payload: SaveAccountRequest = {
      id: editingAccount?.id,
      companyId,
      code: form.code,
      name: form.name,
      type: form.type,
      subtype: form.subtype || null,
      parentAccountId: form.parentAccountId || null,
      normalBalance: form.normalBalance,
      isActive: form.isActive,
      allowsManualEntries: form.allowsManualEntries,
    };

    try {
      await saveAccountAsync(payload);
      setIsModalOpen(false);
      setEditingAccount(null);
    } catch {
      // toast handled by mutation onError
    }
  };

  const handleDeleteConfirm = async () => {
    if (!companyId || !accountToDelete) return;
    try {
      await deleteAccountAsync({ companyId, accountId: accountToDelete.id });
      setAccountToDelete(null);
    } catch {
      // toast handled by mutation onError
    }
  };

  if (accountsQuery.isLoading) {
    return <LoadingState message="Loading chart of accounts..." />;
  }

  return (
    <div className="space-y-6">
      <AccountsSummaryCards summary={summary} />

      <AccountsToolbar
        searchQuery={searchQuery}
        typeFilter={typeFilter}
        isRefreshing={accountsQuery.isFetching && !accountsQuery.isLoading}
        onSearchChange={setSearchQuery}
        onTypeFilterChange={setTypeFilter}
        onRefresh={() => accountsQuery.refetch()}
        onCreate={handleCreate}
      />

      {accountsQuery.isError ? (
        <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {accountsQuery.error?.message}
        </div>
      ) : null}

      {!accounts.length ? (
        <EmptyState
          title="No accounts found"
          description={
            searchQuery || typeFilter !== 'all'
              ? 'Try a different search or filter to find the account you need.'
              : 'Create your first account to start building the chart of accounts.'
          }
          primaryAction={
            !searchQuery && typeFilter === 'all'
              ? { label: 'Create account', onClick: handleCreate }
              : undefined
          }
        />
      ) : (
        <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white">
          <AccountsTable
            accounts={accounts}
            allAccounts={allAccounts}
            onEdit={handleEdit}
            onDelete={setAccountToDelete}
          />
          {pagination ? (
            <PaginationControls
              pagination={pagination}
              itemLabel="accounts"
              onPageChange={setPage}
            />
          ) : null}
        </div>
      )}

      {isModalOpen ? (
        <AccountEditorModal
          title={editingAccount ? 'Edit account' : 'Create account'}
          initial={
            editingAccount
              ? createAccountFormFromRecord(editingAccount)
              : createEmptyAccountForm()
          }
          accounts={allAccounts.filter(
            (account) => !editingAccount || account.id !== editingAccount.id,
          )}
          isSaving={isSaving}
          onClose={() => {
            setIsModalOpen(false);
            setEditingAccount(null);
          }}
          onSubmit={handleSave}
        />
      ) : null}

      <ConfirmDialog
        isOpen={Boolean(accountToDelete)}
        onClose={() => setAccountToDelete(null)}
        onConfirm={handleDeleteConfirm}
        title="Delete account"
        message={
          accountToDelete
            ? `Delete account ${accountToDelete.code} - ${accountToDelete.name}? This action cannot be undone.`
            : ''
        }
        isLoading={isDeleting}
        confirmText="Delete account"
        tone="danger"
      />
    </div>
  );
};

// ─── Journal Entries ──────────────────────────────────────────────────────────

export const JournalEntriesPage: React.FC = () => {
  const currentUser = useAppSelector(selectCurrentUser);
  const companyId = currentUser?.company?.id ?? null;
  const outletId = currentUser?.outlet?.id ?? null;
  const outletName = currentUser?.outlet?.name ?? null;

  const [searchQuery, setSearchQuery] = useState('');
  const deferredSearchQuery = useDeferredValue(searchQuery);
  const [statusFilter, setStatusFilter] = useState<
    'all' | JournalEntryRecord['status']
  >('all');
  const [sourceFilter, setSourceFilter] = useState<
    'all' | JournalEntryRecord['source_module']
  >('all');
  const [dateFrom, setDateFrom] = useState<string | null>(null);
  const [dateTo, setDateTo] = useState<string | null>(null);
  const [isExporting, setIsExporting] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState<JournalEntryRecord | null>(
    null,
  );
  const [initialForm, setInitialForm] = useState<JournalEntryFormState>(
    createEmptyJournalEntryForm(outletId),
  );
  const [entryToDelete, setEntryToDelete] = useState<JournalEntryRecord | null>(
    null,
  );
  const [entryToReverse, setEntryToReverse] =
    useState<JournalEntryRecord | null>(null);
  const [entryToView, setEntryToView] = useState<JournalEntryRecord | null>(
    null,
  );
  const [viewLines, setViewLines] = useState<JournalEntryLineRecord[]>([]);

  const journalsQuery = useJournalEntries({
    companyId: companyId ?? 0,
    page,
    pageSize,
    search: deferredSearchQuery,
    status: statusFilter,
    sourceModule: sourceFilter,
    dateFrom,
    dateTo,
  });

  const optionsQuery = useAccountOptions(companyId);

  const { mutateAsync: saveJournalAsync, isPending: isSaving } =
    useSaveJournalEntry();
  const { mutateAsync: postJournalAsync, isPending: isPosting } =
    usePostJournalEntry();
  const { mutateAsync: reverseJournalAsync, isPending: isReversing } =
    useReverseJournalEntry();
  const { mutateAsync: deleteJournalAsync, isPending: isDeleting } =
    useDeleteJournalEntry();

  const entries = journalsQuery.data?.data ?? [];
  const pagination = journalsQuery.data?.pagination;
  const accounts = optionsQuery.data ?? [];

  useEffect(() => {
    setPage(1);
  }, [deferredSearchQuery, sourceFilter, statusFilter, dateFrom, dateTo]);

  const handleExportCsv = async () => {
    if (!companyId) return;
    setIsExporting(true);
    try {
      const result = await fetchJournalEntries({
        companyId,
        outletId,
        status: statusFilter,
        sourceModule: sourceFilter,
        search: deferredSearchQuery,
        dateFrom,
        dateTo,
        exportAll: true,
        page: 1,
        pageSize: 0,
      });
      if (result.success && result.data) {
        const csv = buildJournalEntriesCsv(result.data.data);
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = `journal-entries-${new Date().toISOString().slice(0, 10)}.csv`;
        anchor.click();
        URL.revokeObjectURL(url);
      }
    } finally {
      setIsExporting(false);
    }
  };

  const handleCreate = () => {
    setEditingEntry(null);
    setInitialForm(createEmptyJournalEntryForm(outletId));
    setIsModalOpen(true);
  };

  const handleEdit = async (entry: JournalEntryRecord) => {
    if (!companyId) {
      apiError('Company details are missing for this account.');
      return;
    }
    try {
      const data = await queryClient.fetchQuery({
        queryKey: queryKeys.accounting.journalDetail(companyId, entry.id),
        queryFn: async () => {
          const response = await fetchJournalEntryById(companyId, entry.id);
          if (!response.success || !response.data?.entry) {
            throw new Error(response.error ?? 'Failed to load journal entry');
          }
          return response.data;
        },
        staleTime: 1000 * 30,
      });
      setEditingEntry(data.entry!);
      setInitialForm(createJournalEntryFormFromRecord(data.entry!, data.lines));
      setIsModalOpen(true);
    } catch (err) {
      apiError(
        err instanceof Error ? err.message : 'Failed to load journal entry',
      );
    }
  };

  const handleView = async (entry: JournalEntryRecord) => {
    if (!companyId) {
      apiError('Company details are missing for this account.');
      return;
    }
    try {
      const data = await queryClient.fetchQuery({
        queryKey: queryKeys.accounting.journalDetail(companyId, entry.id),
        queryFn: async () => {
          const response = await fetchJournalEntryById(companyId, entry.id);
          if (!response.success || !response.data?.entry) {
            throw new Error(
              response.error ?? 'Failed to load journal entry details',
            );
          }
          return response.data;
        },
        staleTime: 1000 * 30,
      });
      setEntryToView(data.entry);
      setViewLines(data.lines);
    } catch (err) {
      apiError(
        err instanceof Error
          ? err.message
          : 'Failed to load journal entry details',
      );
    }
  };

  const handleSaveJournal = async (form: JournalEntryFormState) => {
    if (!companyId) {
      apiError('Company details are missing for this account.');
      return;
    }
    try {
      await saveJournalAsync({ companyId, outletId, editingEntry, form });
      setIsModalOpen(false);
      setEditingEntry(null);
    } catch {
      // toast handled by mutation onError
    }
  };

  const handleDeleteDraft = async () => {
    if (!companyId || !entryToDelete) return;
    try {
      await deleteJournalAsync({ companyId, entryId: entryToDelete.id });
      setEntryToDelete(null);
    } catch {
      // toast handled by mutation onError
    }
  };

  const handlePost = async (entry: JournalEntryRecord) => {
    if (!companyId) {
      apiError('Company details are missing for this account.');
      return;
    }
    try {
      await postJournalAsync({ companyId, entryId: entry.id });
    } catch {
      // toast handled by mutation onError
    }
  };

  const handleReverse = async (values: {
    reversalDate: string;
    memo: string;
  }) => {
    if (!companyId || !entryToReverse) return;
    try {
      await reverseJournalAsync({
        companyId,
        entryId: entryToReverse.id,
        ...values,
      });
      setEntryToReverse(null);
    } catch {
      // toast handled by mutation onError
    }
  };

  if (journalsQuery.isLoading) {
    return <LoadingState message="Loading journal entries..." />;
  }

  return (
    <div className="space-y-6">
      <div className="rounded-2xl border border-gray-200 bg-white p-4">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h2 className="text-lg font-semibold text-black">
              Journal Entries
            </h2>
            <p className="mt-1 text-sm text-gray-600">
              Create draft manual journals, compose lines, and post balanced
              entries into the ledger.
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <AccountingPageInfoButton page="journal-entries" />
            <Button
              variant="outline"
              onClick={() => journalsQuery.refetch()}
              isLoading={journalsQuery.isFetching && !journalsQuery.isLoading}
            >
              Refresh
            </Button>
            <Button
              variant="outline"
              onClick={handleExportCsv}
              isLoading={isExporting}
              disabled={journalsQuery.isLoading}
            >
              Export CSV
            </Button>
            <Button onClick={handleCreate}>Create journal</Button>
          </div>
        </div>
        <div className="mt-3 flex flex-col gap-3">
          <input
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            placeholder="Search memo, bill number, reference type, or reference ID"
            className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-pos-primary/30"
          />
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <DropdownSelect
              value={statusFilter}
              onChange={(v) =>
                setStatusFilter(v as 'all' | JournalEntryRecord['status'])
              }
              options={[
                { value: 'all', label: 'All statuses' },
                { value: 'draft', label: 'Draft' },
                { value: 'posted', label: 'Posted' },
                { value: 'reversed', label: 'Reversed' },
                { value: 'voided', label: 'Voided' },
              ]}
            />
            <DropdownSelect
              value={sourceFilter}
              onChange={(v) =>
                setSourceFilter(
                  v as 'all' | JournalEntryRecord['source_module'],
                )
              }
              options={[
                { value: 'all', label: 'All sources' },
                { value: 'manual', label: 'Manual' },
                { value: 'sales', label: 'Sales' },
                { value: 'inventory', label: 'Inventory' },
                { value: 'accommodation', label: 'Accommodation' },
                { value: 'booking_com', label: 'Booking.com' },
                { value: 'system', label: 'System' },
              ]}
            />
            <DatePicker
              mode="range"
              startDate={dateFrom}
              endDate={dateTo}
              placeholder="Filter by date range"
              onRangeChange={(start, end) => {
                setDateFrom(start);
                setDateTo(end);
              }}
            />
          </div>
        </div>
      </div>

      {journalsQuery.isError ? (
        <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {journalsQuery.error?.message}
        </div>
      ) : null}

      {!entries.length ? (
        <EmptyState
          title="No journal entries found"
          description="Create your first manual journal draft to begin posting adjustments and corrections."
          primaryAction={{ label: 'Create journal', onClick: handleCreate }}
        />
      ) : (
        <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white">
          <JournalEntriesTable
            entries={entries}
            outletName={outletName}
            isFetching={journalsQuery.isFetching}
            onView={handleView}
            onEdit={handleEdit}
            onDelete={setEntryToDelete}
            onPost={handlePost}
            onReverse={setEntryToReverse}
          />
          {pagination ? (
            <PaginationControls
              pagination={pagination}
              itemLabel="journal entries"
              onPageChange={setPage}
            />
          ) : null}
        </div>
      )}

      {isModalOpen ? (
        <JournalEntryEditorModal
          title={editingEntry ? 'Edit journal draft' : 'Create journal draft'}
          initial={initialForm}
          accounts={accounts}
          outletName={outletName}
          isSaving={isSaving}
          onClose={() => {
            setIsModalOpen(false);
            setEditingEntry(null);
          }}
          onSubmit={handleSaveJournal}
        />
      ) : null}

      <ConfirmDialog
        isOpen={Boolean(entryToDelete)}
        onClose={() => setEntryToDelete(null)}
        onConfirm={handleDeleteDraft}
        title="Delete journal draft"
        message={
          entryToDelete
            ? `Delete the draft journal dated ${entryToDelete.entry_date}? This action cannot be undone.`
            : ''
        }
        isLoading={isDeleting}
        confirmText="Delete draft"
        tone="danger"
      />

      {entryToReverse ? (
        <ReverseJournalEntryModal
          entry={entryToReverse}
          isSubmitting={isReversing}
          onClose={() => setEntryToReverse(null)}
          onSubmit={handleReverse}
        />
      ) : null}

      {entryToView ? (
        <JournalEntryDetailModal
          entry={entryToView}
          lines={viewLines}
          accounts={accounts}
          outletName={outletName}
          onClose={() => {
            setEntryToView(null);
            setViewLines([]);
          }}
        />
      ) : null}

      {isPosting ? (
        <div className="rounded-2xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-700">
          Posting journal entry...
        </div>
      ) : null}

      {isReversing ? (
        <div className="rounded-2xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-700">
          Reversing journal entry...
        </div>
      ) : null}
    </div>
  );
};

// ─── Accounts Payable ────────────────────────────────────────────────────────

export const AccountsPayablePage: React.FC = () => <RealAccountsPayablePage />;

// ─── Accounting Reports ───────────────────────────────────────────────────────

export const AccountingReportsPage: React.FC<{
  defaultTab?: AccountingReportTab;
}> = ({ defaultTab }) => {
  const currentUser = useAppSelector(selectCurrentUser);
  const effectiveOutletId = useAppSelector(selectEffectiveOutletId);
  const companyId = currentUser?.company?.id ?? null;

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [activeTab, setActiveTab] = useState<AccountingReportTab>(
    defaultTab ?? 'trial-balance',
  );
  const [searchQuery, setSearchQuery] = useState('');
  const deferredSearchQuery = useDeferredValue(searchQuery);
  const [accountId, setAccountId] = useState('');
  const [sourceModule, setSourceModule] = useState<
    JournalEntryRecord['source_module'] | 'all'
  >('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);

  useEffect(() => {
    setPage(1);
  }, [
    accountId,
    activeTab,
    dateFrom,
    dateTo,
    deferredSearchQuery,
    sourceModule,
  ]);

  const optionsQuery = useAccountOptions(companyId);
  const accounts = optionsQuery.data ?? [];

  const commonParams = {
    companyId: companyId ?? 0,
    outletId: effectiveOutletId,
    search: deferredSearchQuery,
    accountId: accountId || null,
    sourceModule,
    dateFrom: dateFrom || null,
    dateTo: dateTo || null,
    page,
    pageSize,
  };

  const tbQuery = useTrialBalance(
    companyId,
    effectiveOutletId,
    deferredSearchQuery,
    activeTab === 'trial-balance',
  );
  const glQuery = useGeneralLedger(
    commonParams,
    activeTab === 'general-ledger',
  );
  const aaQuery = useAccountActivity(
    commonParams,
    activeTab === 'account-activity',
  );

  const isLoading =
    (activeTab === 'trial-balance' && tbQuery.isLoading) ||
    (activeTab === 'general-ledger' && glQuery.isLoading) ||
    (activeTab === 'account-activity' && aaQuery.isLoading);

  const isRefreshing =
    (activeTab === 'trial-balance' &&
      tbQuery.isFetching &&
      !tbQuery.isLoading) ||
    (activeTab === 'general-ledger' &&
      glQuery.isFetching &&
      !glQuery.isLoading) ||
    (activeTab === 'account-activity' &&
      aaQuery.isFetching &&
      !aaQuery.isLoading);

  const activeError =
    (activeTab === 'trial-balance' && tbQuery.error) ||
    (activeTab === 'general-ledger' && glQuery.error) ||
    (activeTab === 'account-activity' && aaQuery.error) ||
    null;

  const trialBalanceRows = tbQuery.data ?? [];
  const generalLedgerRows = glQuery.data?.data ?? [];
  const accountActivityRows = aaQuery.data?.data ?? [];

  const activePagination =
    activeTab === 'general-ledger'
      ? glQuery.data?.pagination
      : aaQuery.data?.pagination;

  const handleRefresh = () => {
    if (activeTab === 'trial-balance') tbQuery.refetch();
    else if (activeTab === 'general-ledger') glQuery.refetch();
    else aaQuery.refetch();
  };

  const handleExport = () => {
    let content = '';
    let fileName = 'accounting-report.csv';

    if (activeTab === 'trial-balance') {
      content = buildTrialBalanceCsv(trialBalanceRows);
      fileName = 'trial-balance.csv';
    } else if (activeTab === 'general-ledger') {
      content = buildGeneralLedgerCsv(generalLedgerRows);
      fileName = 'general-ledger.csv';
    } else {
      content = buildAccountActivityCsv(accountActivityRows);
      fileName = 'account-activity.csv';
    }

    const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = fileName;
    anchor.click();
    URL.revokeObjectURL(url);
  };

  if (isLoading) {
    return <LoadingState message="Loading accounting reports..." />;
  }

  const showEmptyState =
    activeTab === 'trial-balance'
      ? !trialBalanceRows.length
      : activeTab === 'general-ledger'
        ? !generalLedgerRows.length
        : !accountActivityRows.length;

  return (
    <div className="space-y-6">
      <AccountingReportsToolbar
        activeTab={activeTab}
        searchQuery={searchQuery}
        dateFrom={dateFrom}
        dateTo={dateTo}
        sourceModule={sourceModule}
        accountId={accountId}
        accounts={accounts}
        isRefreshing={isRefreshing}
        canExport={!showEmptyState}
        onSearchChange={setSearchQuery}
        onDateFromChange={setDateFrom}
        onDateToChange={setDateTo}
        onSourceModuleChange={setSourceModule}
        onAccountChange={setAccountId}
        onRefresh={handleRefresh}
        onExport={handleExport}
      />

      {activeError ? (
        <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {activeError.message}
        </div>
      ) : null}

      {showEmptyState ? (
        <EmptyState
          title="No report rows found"
          description="Adjust the current filters or refresh once new accounting activity has been posted."
        />
      ) : (
        <div className="space-y-4">
          {activeTab === 'trial-balance' ? (
            <TrialBalanceTable rows={trialBalanceRows} />
          ) : null}
          {activeTab === 'general-ledger' ? (
            <GeneralLedgerTable rows={generalLedgerRows} />
          ) : null}
          {activeTab === 'account-activity' ? (
            <AccountActivityTable rows={accountActivityRows} />
          ) : null}

          {activeTab !== 'trial-balance' && activePagination ? (
            <PaginationControls
              pagination={activePagination}
              itemLabel={
                activeTab === 'general-ledger' ? 'ledger rows' : 'activity rows'
              }
              onPageChange={setPage}
            />
          ) : null}
        </div>
      )}
    </div>
  );
};

// ─── Settings ─────────────────────────────────────────────────────────────────

export const AccountingSettingsPage: React.FC = () => (
  <FinancialPostingRulesSettingsPage />
);

export {
  AccountingPeriodsSettingsPage,
  FinancialPostingRulesSettingsPage,
  PaymentMappingsSettingsPage,
  TaxRatesSettingsPage,
  TaxRefundsSettingsPage,
};

// ─── Individual ledger report page wrappers ───────────────────────────────────

export const TrialBalancePage: React.FC = () => (
  <AccountingReportsPage defaultTab="trial-balance" />
);

export const GeneralLedgerPage: React.FC = () => (
  <AccountingReportsPage defaultTab="general-ledger" />
);

export const AccountActivityPage: React.FC = () => (
  <AccountingReportsPage defaultTab="account-activity" />
);
